// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { awBorderGrey } from 'Utils/themes';

const styles = (theme: any) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${awBorderGrey}`,
    borderRadius: '12px',
    padding: '20px',
    cursor: 'pointer',
    boxShadow: '2px 6px 4px rgba(0,0,0,0.1)',
    transition: theme.transitions.create(['box-shadow', 'border-color']),
    // On a phone the readings wrap under the name instead of squeezing it to nothing
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      rowGap: theme.spacing(1.5),
    },
    '&:hover': {
      boxShadow: '2px 6px 10px rgba(0,0,0,0.16)',
    },
  },
  rowSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}33`,
  },
  // Lifted above its neighbours and the pinned controls band while being dragged onto a folder,
  // so it is not clipped by the rows below it or hidden as it passes the band.
  // Written out rather than using @dnd-kit/utilities' CSS helper: that package is only a
  // transitive dependency here, and this is the one string it would have produced.
  rowDragging: (transform: { x: number; y: number } | null) => ({
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    zIndex: 2,
    position: 'relative',
  }),
  identityGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexBasis: '100%',
    },
  },
  thumbnail: {
    width: '58.6px',
    height: '50px',
    borderRadius: '8px',
    border: `1px solid ${awBorderGrey}`,
    objectFit: 'cover',
    flexShrink: 0,
  },
  // Sized only when the project is being worked on: an empty column would take width from
  // every other row's name for a bar most rows never draw.
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },
  // Matches the grid card and the map rail exactly - same size, weight and tracking.
  name: {
    fontSize: '18px',
    fontWeight: 500,
    letterSpacing: '0.6857px',
    color: theme.palette.text.primary,
    // No fixed cap: the name takes what the row leaves it and ellipsizes, so it uses the
    // space a wide screen offers instead of stopping at an arbitrary width.
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  statusDot: (color: string) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: color,
    flexShrink: 0,
  }),
  statusLabel: {
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      rowGap: theme.spacing(0.5),
    },
  },
  // The design fades this line rather than giving it a grey of its own.
  metaText: {
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.text.primary,
    opacity: 0.7,
    whiteSpace: 'nowrap',
  },
  metaLabel: {
    fontWeight: 500,
  },
  createdGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  calendarIcon: {
    fontSize: '18px',
    color: theme.palette.text.primary,
    opacity: 0.7,
  },
  values: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '30px',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
    },
  },
  // Fixed width, not content width: the spec draws one row in isolation, but down a list
  // content-sized columns land at a different x on every row ("Sep 4, 2026" against
  // "May 24, 2022"), so the readings never line up. Wide enough for the longest date the
  // formatter produces.
  valueColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    width: '104px',
    flexShrink: 0,
    // Wrapped under the name, the readings line up by their own row and need no fixed column
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
    },
  },
  value: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  // Much wider than the date columns: the longest status names its pipeline stage too
  // ("In Progress - Processing"), and truncating it would cost the reading the stage is
  // there for.
  statusColumn: {
    width: '208px',
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
    },
  },
  valueLabel: {
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
  menuButton: {
    flexShrink: 0,
    width: '30px',
    height: '30px',
    color: theme.palette.text.primary,
  },
  menuItem: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#484d52',
  },
});

export default styles;
